import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import "./styles.scss";
import { Popconfirm, Space, Spin, Table } from "antd";
import { Scrollbars } from "react-custom-scrollbars";

import { Button, Input, PageHeader, UploadCard } from "../../components";
import { GetIcon } from "../../config/icon";
import { CheckValidity } from "../../utils";
import { uploadImage } from "../../api";
import { OpenNotification } from "../../config/notification";
import {
  cooCreateNewBlog,
  cooDeleteBlog,
  cooGetAllBlog,
  cooUpdateBlog,
} from "./service/blog.service";

const initialForm = () => ({
  title: {
    label: "Title",
    key: "title",
    elementType: "input",
    elementConfig: {
      type: "text",
      placeholder: "Ex : Top 5 credit card offers this month",
    },
    value: "",
    validation: { required: true },
    isValid: false,
    touched: false,
    invalidReason: "",
  },
  content: {
    label: "Content",
    key: "content",
    elementType: "textArea",
    elementConfig: {
      type: "text",
      placeholder: "Write the blog content here...",
    },
    value: "",
    validation: { required: true },
    isValid: false,
    touched: false,
    invalidReason: "",
  },
});

const Blog = (props) => {
  const [dataForm, setDataForm] = useState(initialForm());
  const [imageURL, setImageURL] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [editBlogId, setEditBlogId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewPointHeight, setViewPointHeight] = useState(window.innerHeight);

  useEffect(() => {
    const updateWindowDimensions = () => setViewPointHeight(window.innerHeight);
    window.addEventListener("resize", updateWindowDimensions);
    getBlogs();
    return () => window.removeEventListener("resize", updateWindowDimensions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBlogs = async () => {
    setIsLoading(true);
    try {
      let res = await cooGetAllBlog();
      setDataSource((res && res.data && res.data.data) || []);
    } catch (e) {
      OpenNotification("Error", "Unable to load blogs", "error");
    }
    setIsLoading(false);
  };

  //====== INPUT COMMON HANDLE CHANGE
  const handleChange = async (value, inputIdentity) => {
    const updateForm = { ...dataForm };
    updateForm[inputIdentity].value = value;
    let validityRes = await CheckValidity(
      inputIdentity,
      value,
      updateForm[inputIdentity].validation,
    );
    if (validityRes) {
      updateForm[inputIdentity].isValid = validityRes.isValid;
      updateForm[inputIdentity].invalidReason = validityRes.reason;
      updateForm[inputIdentity].touched = true;
    }
    setDataForm(updateForm);
  };

  const updateFormValidity = async (inputIdentity) => {
    const updateForm = { ...dataForm };
    let validityRes = await CheckValidity(
      inputIdentity,
      updateForm[inputIdentity].value,
      updateForm[inputIdentity].validation,
    );
    updateForm[inputIdentity].isValid = validityRes.isValid;
    updateForm[inputIdentity].touched = true;
    setDataForm(updateForm);
  };

  //====== IMAGE SELECT & UPLOAD
  const onSelectFile = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setIsImageUploading(true);
    try {
      const base64 = await new Promise((resolve) => {
        let fileReader = new FileReader();
        fileReader.onload = () => resolve(fileReader.result);
        fileReader.readAsDataURL(file);
      });
      let res = await uploadImage(base64, "blog", "BLOG");
      if (res && res.data && res.data.status) {
        setImageURL(res.data.data.URL);
      } else {
        OpenNotification("Error", "Image upload failed", "error");
      }
    } catch (err) {
      OpenNotification("Error", "Image upload failed", "error");
    }
    setIsImageUploading(false);
  };

  const clearAll = () => {
    setDataForm(initialForm());
    setImageURL("");
    setEditBlogId(null);
    setIsSaving(false);
  };

  const buildBody = () => ({
    title: dataForm.title.value,
    image_url: imageURL,
    content: dataForm.content.value,
    status: "ACTIVE",
  });

  const isFormValid = async () => {
    await updateFormValidity("title");
    await updateFormValidity("content");
    if (!dataForm.title.value || !dataForm.content.value) {
      OpenNotification("Error", "Title and content are required", "error");
      return false;
    }
    if (!imageURL) {
      OpenNotification("Error", "Please upload an image", "error");
      return false;
    }
    return true;
  };

  //====== SAVE / UPDATE
  const onClickSubmit = async () => {
    if (!(await isFormValid())) return;
    setIsSaving(true);
    try {
      let res = editBlogId
        ? await cooUpdateBlog(editBlogId, buildBody())
        : await cooCreateNewBlog(buildBody());
      if (res && res.data && res.data.status) {
        OpenNotification("Success", res.data.message, "success");
        clearAll();
        await getBlogs();
      } else {
        OpenNotification(
          "Error",
          (res && res.data && res.data.message) || "Failed",
          "error",
        );
        setIsSaving(false);
      }
    } catch (e) {
      OpenNotification("Error", "Something went wrong", "error");
      setIsSaving(false);
    }
  };

  const onClickEdit = (record) => {
    const updateForm = initialForm();
    updateForm.title.value = record.title || "";
    updateForm.content.value = record.content || "";
    setDataForm(updateForm);
    setImageURL(record.imageURL || "");
    setEditBlogId(record.blogId || record._id || record.id);
  };

  const onClickDelete = async (record) => {
    try {
      let res = await cooDeleteBlog(record.blogId || record._id || record.id);
      if (res && res.data && res.data.status) {
        OpenNotification("Success", res.data.message, "success");
        if (
          editBlogId &&
          editBlogId === (record.blogId || record._id || record.id)
        ) {
          clearAll();
        }
        await getBlogs();
      } else {
        OpenNotification("Error", "Delete failed", "error");
      }
    } catch (e) {
      OpenNotification("Error", "Delete failed", "error");
    }
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "imageURL",
      key: "imageURL",
      width: 90,
      render: (url) =>
        url ? (
          <img className={"cco__blog-thumb"} src={url} alt="blog" />
        ) : (
          <div className={"cco__blog-thumb empty"} />
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Content",
      dataIndex: "content",
      key: "content",
      render: (text) => (
        <span className={"cco__blog-content-cell"}>
          {text && text.length > 80 ? `${text.substring(0, 80)}...` : text}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 130,
      render: (text, record) => (
        <Space size="middle">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onClickEdit(record);
            }}
          >
            Edit
          </a>
          <Popconfirm
            title="Delete this blog?"
            onConfirm={() => onClickDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className={"cco__blog-delete"}
            >
              Delete
            </a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className={"cco__blog-wrapper"}>
      <div className={"cco__page-header-wrapper"}>
        <PageHeader
          option={"d-flex justify-content-between"}
          isRefresh={true}
          isDate={false}
          isLoading={isLoading}
          onClickRefresh={async () => await getBlogs()}
          title={"Blogs"}
        />
      </div>

      <div className={"cco__blog-body d-flex"}>
        {/*---- CREATE / EDIT FORM ----*/}
        <div className={"cco__blog-form-section"}>
          <div className={"cco__cnc-right-content-wrapper"}>
            <PageHeader
              option={"sm"}
              title={editBlogId ? "Edit blog" : "Create new blog"}
            />
            <div className={"cco_common-form-content-wrapper"}>
              <Input
                elementConfig={dataForm.title.elementConfig}
                elementType={dataForm.title.elementType}
                required={dataForm.title.validation.required}
                isValid={!dataForm.title.isValid}
                touched={dataForm.title.touched}
                value={dataForm.title.value}
                props={dataForm.title}
                isShowRequired={true}
                size={"md"}
                margin={"m-b-2"}
                label={dataForm.title.label}
                changed={async (e) => {
                  e.preventDefault();
                  await handleChange(e.target.value, dataForm.title.key);
                }}
              />

              {/*---- IMAGE ----*/}
              <UploadCard
                title={"Image"}
                logoURL={imageURL}
                isImageUploading={isImageUploading}
                onSelectFile={onSelectFile}
              />
              {imageURL && (
                <div className={"cco__blog-image-preview m-b-2"}>
                  <img src={imageURL} alt="blog cover" />
                  <div
                    className={"cco__blog-image-remove"}
                    onClick={() => setImageURL("")}
                  >
                    Remove image
                  </div>
                </div>
              )}

              <Input
                elementConfig={dataForm.content.elementConfig}
                elementType={dataForm.content.elementType}
                required={dataForm.content.validation.required}
                isValid={!dataForm.content.isValid}
                touched={dataForm.content.touched}
                value={dataForm.content.value}
                props={dataForm.content}
                isShowRequired={true}
                size={"md"}
                rows={8}
                margin={"m-b-2"}
                label={dataForm.content.label}
                changed={async (e) => {
                  e.preventDefault();
                  await handleChange(e.target.value, dataForm.content.key);
                }}
              />

              <div className={"login-footer-action-wrapper d-flex mt-4"}>
                <Button
                  leftIcon={false}
                  rightIcon={false}
                  isLoading={isSaving}
                  isActiveAnimation={false}
                  reactRightIcon={GetIcon("leftArrow")}
                  onClick={async () => await onClickSubmit()}
                  text={editBlogId ? "Update" : "Submit"}
                  size={"md"}
                  type={"btn-block"}
                />
              </div>
              {editBlogId && (
                <div className={"mt-2"}>
                  <Button
                    leftIcon={false}
                    rightIcon={false}
                    isActiveAnimation={false}
                    reactRightIcon={GetIcon("leftArrow")}
                    onClick={() => clearAll()}
                    text={"Cancel"}
                    size={"full-width-100"}
                    type={"btn-delete"}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/*---- LIST ----*/}
        <div className={"cco__blog-list-section"}>
          <Scrollbars
            style={{ height: viewPointHeight - 210 }}
            thumbMinSize={30}
            universal={true}
            autoHide
          >
            <div className={"cco__page-body-wrapper"}>
              <Spin spinning={isLoading}>
                <Table
                  rowKey={(record) => record.blogId || record._id || record.id}
                  dataSource={dataSource || []}
                  columns={columns}
                  pagination={{ pageSize: 10 }}
                />
              </Spin>
            </div>
          </Scrollbars>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    userData: state.userReducer.data,
  };
};

export default connect(mapStateToProps)(Blog);
